// Procedural karambit viewmodel with a fire & ice (red-to-blue marbled) blade, held by the CC-BY
// "Fps Rig" arms (fpsRig.ts) once they load; the pistol is that rig's Glock.
// Rendered in a second pass with its own scene and camera; everything here is in camera space.

import * as THREE from 'three';
import { FINISHES, type Finish } from './finishes';
import { drawFinish } from './finishTexture';
import { loadKarambit, type KnifeModel } from './karambitModel';
import { loadButterfly } from './butterflyModel';
import { loadM9 } from './m9Model';
import { loadSkeleton } from './skeletonModel';
import { loadKukri } from './kukriModel';
import { loadTalon } from './talonModel';
import { loadBowie } from './bowieModel';
import { loadStiletto } from './stilettoModel';
import type { KnifeKind } from './knives';
import { loadFpArmsKnife } from './fpArms';
import { noHighlights } from './noHighlights';
import { LEGACY_LIGHT_SCALE, legacyPointLight } from './legacyLook';
import { buildArm, buildMuzzleFlash, buildPistol, type PistolModel } from './weaponModels';
import type { WeaponAssets } from '../models/cssWeapons';
import { StudioWeapon, WeaponTextures } from './studioWeapon';
import { loadFpsRig, type FpsRig } from './fpsRig';
import { animLength, P_TWIST, POSE_SIZE, sampleKnife, type KnifeAnim } from './knifeAnim';

export { FINISHES, type Finish } from './finishes';

function finishTexture(finish: Finish, anisotropy: number) {
  const t = new THREE.CanvasTexture(drawFinish(finish));
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  // Shape coords: x 0.9..1.9, y -0.6..0.25 -> uv 0..1
  t.repeat.set(1, 1 / 0.85);
  t.offset.set(-0.9, 0.6 / 0.85);
  t.anisotropy = anisotropy;
  return t;
}

/** Every knife's model loader (a new knife must be added here: the type insists). */
const KNIFE_LOADERS: Record<KnifeKind, (blade: THREE.Material) => Promise<KnifeModel | null>> = {
  karambit: loadKarambit,
  butterfly: loadButterfly,
  m9: loadM9,
  skeleton: loadSkeleton,
  kukri: loadKukri,
  talon: loadTalon,
  bowie: loadBowie,
  stiletto: loadStiletto,
};

const ease = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t * t * (3 - 2 * t));

/**
 * The butterfly's inspect flip: while the knife is held up (knifeAnim's inspect), the blade and
 * the second handle swing round the first handle's pin, a full turn one way and then back.
 */
function butterflyFlip(t: number) {
  return Math.PI * 2 * (ease((t - 0.6) / 0.55) - ease((t - 1.35) / 0.55));
}
/** Pistol inspect length (s). */
const PISTOL_INSPECT = 2.4;
/** The pistol's resting angle in the hand: muzzle a little toward the middle and up. */
const PISTOL_REST_YAW = 0.12;
const PISTOL_REST_PITCH = 0.06;
/** Seconds to blend into a new knife animation from wherever the last one was. */
const KNIFE_BLEND = 0.06;
const REST = { x: 0.5, y: -0.4, z: -0.85, rx: -0.3, ry: -0.4, rz: 0.45 };
/** Source units -> viewmodel camera units for the CS:S models (any scale works; this suits near/far). */
const STUDIO_SCALE = 0.02;

/** Horizontal FOV at 4:3 (how Source gives viewmodel_fov) -> vertical FOV. */
const verticalFov = (h: number) => (2 * Math.atan(Math.tan((h * Math.PI) / 360) * 0.75) * 180) / Math.PI;

interface Studio {
  /** Camera-space holder that bob and sway move. */
  offset: THREE.Group;
  knife: StudioWeapon | null;
  usp: StudioWeapon | null;
  textures: WeaponTextures;
  flash: THREE.Group;
}

/** What the viewmodel needs from the player each frame. */
export interface ViewmodelInput {
  speed: number;
  onGround: boolean;
  /** Vertical velocity (Source Z). */
  vz: number;
  /** Landing impact to consume (downward speed), 0 if none. */
  landImpact: number;
}

export class Viewmodel {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(54, 1, 0.01, 10);
  private readonly root = new THREE.Group();
  /** Mirrors the whole viewmodel for left-handed view. */
  private readonly hand = new THREE.Group();
  private bladeMat: THREE.MeshPhongMaterial | null = null;
  /** The knife in the hand (karambit or butterfly), for its accent colour. */
  private karambit: KnifeModel | null = null;
  private knifeKind: KnifeKind = 'karambit';
  /** Resolves once the rig arms are in (or failed to load). */
  private rigReady: Promise<void>;
  /** The butterfly's blade and second handle, turning on the first handle's pin for the flip. */
  private swing: THREE.Object3D | null = null;
  private finish: Finish;
  /** Pistol: its own root so it has its own pose; shares the hand group (left-handed mirroring). */
  private readonly pistolRoot = new THREE.Group();
  private knifeArm: THREE.Group;
  private readonly pistol: PistolModel;
  private readonly flash = buildMuzzleFlash();
  private weapon: 'knife' | 'usp' = 'knife';
  /** 0..1 raise animation after switching. */
  private raise = 1;
  private raiseTime = 0.5;
  private fireT = -1;
  private reloadT = -1;
  private reloadDur = 2.7;
  private silencerT = -1;
  private silencerDur = 2.4;
  private silencerAfter = true;
  private flashLeft = 0;
  private bladeTex: THREE.Texture | null = null;
  private readonly tilt = new THREE.Group();
  private readonly spin = new THREE.Group();
  private studio: Studio | null = null;
  private studioWanted = true;
  private silenced = true;
  private slash = 0;
  private fovSetting = 54;
  private readonly st = { swayX: 0, swayY: 0, bob: 0, land: 0, inspect: -1, attack: -1, kind: 0 };
  /** The karambit (with the built-in arm), hidden once the rig arms are in. */
  private knifeModel: THREE.Group | null = null;
  private rig: FpsRig | null = null;
  /** Pistol inspect time (s), -1 when not inspecting. */
  private pistolInspectT = -1;
  /** This reload is the rare flip-up one. */
  private rareReload = false;
  /** Rig knife: current keyframed animation and time into it. */
  private knifeAnim: KnifeAnim = 'idle';
  private knifeT = 0;
  /** Running time for the free hand's idle movement. */
  private idleClock = 0;
  private readonly knifePose: number[] = new Array<number>(POSE_SIZE).fill(0);
  /** Pose when the current animation started: blended out over KNIFE_BLEND so nothing snaps. */
  private readonly knifeFrom: number[] = new Array<number>(POSE_SIZE).fill(0);
  private knifeBlend = false;
  /** Called when the rig arms have loaded (to upload them to the GPU before they're needed). */
  onRigLoaded: (() => Promise<void> | void) | null = null;
  /** The rig gun's resting place in camera space: its turns pivot here, not at the eye. */
  private readonly gunPivot = new THREE.Vector3();
  private readonly turnQ = new THREE.Quaternion();
  private readonly turnE = new THREE.Euler();
  private readonly turnV = new THREE.Vector3();

  constructor(
    private readonly anisotropy: number,
    finish: Finish = 'fireice',
  ) {
    const s = LEGACY_LIGHT_SCALE;
    this.scene.add(new THREE.HemisphereLight(0xdfe8ff, 0x2a2018, 0.75 * s));
    const key = new THREE.DirectionalLight(0xffffff, 0.95 * s);
    key.position.set(-1, 1.6, 1.2);
    this.scene.add(key);
    const rim = legacyPointLight(0xa8d4ff, 0.9, 6);
    rim.position.set(1.1, 0.5, 0.2);
    this.scene.add(rim);
    const warm = legacyPointLight(0xffa060, 0.5, 6);
    warm.position.set(0.2, -0.9, 0);
    this.scene.add(warm);

    const model = new THREE.Group();
    this.root.add(this.tilt);
    this.tilt.add(this.spin);
    this.spin.add(model);
    this.hand.add(this.root);
    // Gloved hand on the knife handle.
    this.knifeArm = buildArm();
    this.root.add(this.knifeArm);
    // Pistol with a gloved hand on the grip.
    this.pistol = buildPistol();
    const pistolArm = buildArm();
    pistolArm.position.set(0, -0.012, 0.03);
    this.pistol.group.add(pistolArm);
    this.pistol.muzzle.add(this.flash);
    this.pistolRoot.add(this.pistol.group);
    this.pistolRoot.visible = false;
    this.hand.add(this.pistolRoot);
    this.scene.add(this.hand);

    const fiTex = finishTexture(finish, anisotropy);
    this.bladeTex = fiTex;
    const bladeMat = new THREE.MeshPhongMaterial({
      map: fiTex,
      emissiveMap: fiTex,
      emissive: 0x2a2a2a,
    });
    this.bladeMat = bladeMat;
    this.finish = finish;
    const steelMat = new THREE.MeshPhongMaterial({ color: 0xaeb6c2, specular: 0xffffff, shininess: 120 });
    const gripMat = new THREE.MeshPhongMaterial({ color: 0x1a1c21, specular: 0x3a3f48, shininess: 18 });
    const ext = (shape: THREE.Shape, depth: number, bevel: number) => {
      const geo = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: true,
        bevelThickness: bevel,
        bevelSize: bevel * 0.85,
        bevelSegments: 3,
        curveSegments: 36,
      });
      geo.translate(-0.45, 0, -depth / 2);
      return geo;
    };
    // Hooked claw blade, sharpened on the inner curve
    const blade = new THREE.Shape();
    blade.moveTo(0.92, 0.12);
    blade.bezierCurveTo(1.35, 0.22, 1.78, 0.02, 1.86, -0.56);
    blade.bezierCurveTo(1.66, -0.24, 1.34, -0.12, 0.92, -0.11);
    blade.lineTo(0.92, 0.12);
    model.add(new THREE.Mesh(ext(blade, 0.03, 0.014), bladeMat));
    // Bolster
    const guard = new THREE.Shape();
    guard.moveTo(0.85, -0.15);
    guard.lineTo(0.96, -0.14);
    guard.lineTo(0.96, 0.15);
    guard.lineTo(0.85, 0.16);
    guard.lineTo(0.85, -0.15);
    model.add(new THREE.Mesh(ext(guard, 0.1, 0.015), steelMat));
    // Curved grip with finger grooves
    const grip = new THREE.Shape();
    grip.moveTo(0, 0.12);
    grip.bezierCurveTo(0.3, 0.16, 0.6, 0.15, 0.87, 0.13);
    grip.lineTo(0.87, -0.13);
    grip.bezierCurveTo(0.75, -0.13, 0.7, -0.2, 0.6, -0.17);
    grip.bezierCurveTo(0.5, -0.13, 0.45, -0.21, 0.33, -0.17);
    grip.bezierCurveTo(0.22, -0.13, 0.1, -0.15, 0, -0.12);
    grip.lineTo(0, 0.12);
    model.add(new THREE.Mesh(ext(grip, 0.08, 0.022), gripMat));
    // Safety ring
    const ring = new THREE.Shape();
    ring.absarc(-0.17, 0, 0.19, 0, Math.PI * 2, false);
    const hole = new THREE.Path();
    hole.absarc(-0.17, 0, 0.115, 0, Math.PI * 2, true);
    ring.holes.push(hole);
    model.add(new THREE.Mesh(ext(ring, 0.07, 0.02), steelMat));
    // Rivets
    for (const rx of [0.28, 0.62]) {
      const r = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.13, 16), steelMat);
      r.rotation.x = Math.PI / 2;
      r.position.set(rx - 0.45, 0, 0);
      model.add(r);
    }
    noHighlights(model);
    model.rotation.set(Math.PI, 0, Math.PI / 2, 'ZYX');
    model.scale.setScalar(0.27);
    // The hand holds the handle: grip axis (-Y of the arm model) along the handle (model X).
    this.root.remove(this.knifeArm);
    model.add(this.knifeArm);
    this.knifeArm.scale.setScalar(1 / 0.27);
    this.knifeArm.rotation.set(-Math.PI / 2, 0, -Math.PI / 2);
    this.knifeArm.position.set(0.27, 0.02, 0);
    this.knifeModel = model;

    // The rig arms replace the built-in ones when they've loaded (the karambit goes in their fist).
    // The knife in their hand is the karambit model (CC-BY, karambitModel.ts), or the procedural one
    // if that didn't load.
    const procedural = new THREE.Group();
    for (const c of model.children) if (c !== this.knifeArm) procedural.add(c.clone());
    this.rigReady = loadKarambit(bladeMat)
      .then((k) => {
        if (k) noHighlights(k.object);
        this.karambit = k;
        k?.setAccent((FINISHES[this.finish] ?? FINISHES.fireice).accent);
        return loadFpsRig(k?.object ?? procedural, bladeMat);
      })
      .then((rig) => {
        if (rig) this.installRig(rig);
      });
  }

  private installRig(rig: FpsRig) {
    this.rig = rig;
    this.placeKnifeArm(rig.knife.object);
    if (this.knifeModel) this.knifeModel.visible = false;
    // Pistol: its own holder; the muzzle flash moves to the rig's barrel.
    this.hand.add(rig.pistol.object);
    rig.pistol.object.updateMatrixWorld(true);
    rig.pistol.object.worldToLocal(rig.pistol.muzzle.getWorldPosition(this.gunPivot));
    // Halfway between the muzzle and the eye's line: about the middle of the gun.
    this.gunPivot.z *= 0.85;
    const flashHolder = new THREE.Object3D();
    flashHolder.scale.setScalar(1 / rig.pistol.muzzle.getWorldScale(new THREE.Vector3()).x);
    rig.pistol.muzzle.add(flashHolder);
    flashHolder.add(this.flash);
    rig.pistol.mixer.addEventListener('finished', () => rig.pistol.idle.reset().play());
    this.setSilenced(this.silenced);
    this.refreshStudio();
    this.onRigLoaded?.();
  }

  /** Knife arm: under the animated knife groups, placed so it rests where it was tuned. */
  private placeKnifeArm(arm: THREE.Object3D) {
    const rest = new THREE.Matrix4()
      .compose(
        new THREE.Vector3(REST.x, REST.y, REST.z),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(REST.rx, REST.ry, REST.rz)),
        new THREE.Vector3(1, 1, 1),
      )
      .invert();
    rest.decompose(arm.position, arm.quaternion, arm.scale);
    this.spin.add(arm);
  }

  /**
   * Hold another knife (every finish works on every knife). Loads its model and builds the knife arm
   * round it, then swaps the arm in; the old knife stays in the hand until then.
   */
  async setKnife(kind: KnifeKind) {
    if (kind === this.knifeKind) return;
    this.knifeKind = kind;
    await this.rigReady;
    const model = await KNIFE_LOADERS[kind](this.bladeMat!);
    if (!model || this.knifeKind !== kind || !this.rig) return;
    noHighlights(model.object);
    const arms = await loadFpArmsKnife(model.object);
    if (!arms || this.knifeKind !== kind || !this.rig) return;
    // In the scene but hidden until it's on the GPU and its shaders are compiled (onRigLoaded warms
    // it): drawn straight away, the new arm and knife would upload and compile mid-frame.
    arms.object.visible = false;
    this.placeKnifeArm(arms.object);
    await this.onRigLoaded?.();
    if (this.knifeKind !== kind || !this.rig) {
      arms.object.removeFromParent();
      return;
    }
    arms.object.visible = true;
    this.rig.knife.object.removeFromParent();
    this.rig.knife = arms;
    this.karambit = model;
    model.setAccent((FINISHES[this.finish] ?? FINISHES.fireice).accent);
    this.swing = arms.knife.getObjectByName('swing') ?? null;
    this.playKnife('draw');
  }

  setFinish(finish: Finish) {
    this.finish = finish;
    this.karambit?.setAccent((FINISHES[finish] ?? FINISHES.fireice).accent);
    if (!this.bladeMat) return;
    const t = finishTexture(finish, this.anisotropy);
    this.bladeMat.map = t;
    this.bladeMat.emissiveMap = t;
    this.bladeMat.needsUpdate = true;
    this.bladeTex?.dispose();
    this.bladeTex = t;
  }

  /** Vertical FOV of the viewmodel camera in degrees (default 54). */
  setFov(fov: number) {
    this.fovSetting = fov;
    // The built-in models were tuned with the setting as a vertical FOV; CS:S models use Source's.
    this.camera.fov = this.studioActive ? verticalFov(fov) : fov;
    this.camera.updateProjectionMatrix();
  }

  /** True while the CS:S models are shown instead of the built-in ones. */
  get studioActive(): boolean {
    return this.studio !== null && this.studioWanted && this.studio[this.weapon] !== null;
  }

  /** Use the CS:S models when they're available (setting). */
  setUseStudio(on: boolean) {
    this.studioWanted = on;
    this.refreshStudio();
  }

  /** Install (or remove, with null) the CS:S weapon models from the player's game files. */
  setStudioWeapons(assets: WeaponAssets | null) {
    if (this.studio) {
      this.hand.remove(this.studio.offset);
      this.studio.knife?.dispose();
      this.studio.usp?.dispose();
      this.studio.textures.dispose();
      this.studio = null;
    }
    if (assets) {
      const textures = new WeaponTextures(assets, this.anisotropy);
      const make = (w: 'knife' | 'usp') => {
        const m = assets.models[w];
        if (!m) return null;
        const sw = new StudioWeapon(m, assets.materials[w] ?? [], textures);
        // Models built left-handed (stock CS:S USP) are mirrored like cl_righthand 1 does.
        if (assets.rightHanded[w] === false) sw.group.scale.y = -1;
        return sw;
      };
      const knife = make('knife');
      if (knife) noHighlights(knife.group);
      const usp = make('usp');
      const offset = new THREE.Group();
      const root = new THREE.Group();
      // Source view space (X forward, Y left, Z up) -> camera space (-Z forward, X right, Y up).
      const k = STUDIO_SCALE;
      root.matrixAutoUpdate = false;
      root.matrix.set(0, -k, 0, 0, 0, 0, k, 0, -k, 0, 0, 0, 0, 0, 0, 1);
      if (knife) root.add(knife.group);
      if (usp) root.add(usp.group);
      offset.add(root);
      // Muzzle flash on the USP's muzzle attachment.
      const flash = buildMuzzleFlash();
      const att = usp?.attachment('1');
      const bone = att ? usp?.bone(att.bone) : undefined;
      if (att && bone) {
        const holder = new THREE.Object3D();
        const l = att.local;
        holder.matrixAutoUpdate = false;
        holder.matrix.set(
          l[0],
          l[1],
          l[2],
          l[3],
          l[4],
          l[5],
          l[6],
          l[7],
          l[8],
          l[9],
          l[10],
          l[11],
          0,
          0,
          0,
          1,
        );
        // The flash points down -Z; this attachment's forward (along the barrel) is +Z.
        flash.rotation.y = Math.PI;
        flash.scale.setScalar(0.5 / k);
        holder.add(flash);
        bone.add(holder);
      }
      this.studio = { offset, knife, usp, textures, flash };
      this.hand.add(offset);
      usp?.setIdle(this.silenced ? 'idle' : 'idle_unsil');
    }
    this.refreshStudio();
  }

  private refreshStudio() {
    const on = this.studioActive;
    if (this.studio) {
      this.studio.offset.visible = on;
      if (this.studio.knife) this.studio.knife.group.visible = this.weapon === 'knife';
      if (this.studio.usp) this.studio.usp.group.visible = this.weapon === 'usp';
    }
    this.root.visible = !on && this.weapon === 'knife';
    this.pistolRoot.visible = !on && this.weapon === 'usp' && !this.rig;
    if (this.rig) this.rig.pistol.object.visible = !on && this.weapon === 'usp';
    this.setFov(this.fovSetting);
  }

  setLeftHanded(left: boolean) {
    this.hand.scale.x = left ? -1 : 1;
  }

  setAspect(aspect: number) {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  sway(dx: number, dy: number) {
    const st = this.st;
    st.swayX = Math.max(-0.05, Math.min(0.05, st.swayX + dx * 0.00022));
    st.swayY = Math.max(-0.04, Math.min(0.04, st.swayY + dy * 0.00022));
  }

  /** Show a weapon, raising it over `seconds` (the deploy time). */
  setWeapon(w: 'knife' | 'usp', seconds: number) {
    this.weapon = w;
    this.raise = 0;
    this.raiseTime = Math.max(0.05, seconds);
    this.fireT = this.reloadT = this.silencerT = -1;
    this.pistolInspectT = -1;
    this.st.inspect = this.st.attack = -1;
    if (w === 'knife') this.playKnife('draw');
    this.refreshStudio();
    if (this.studio) {
      if (w === 'knife') this.studio.knife?.play('draw');
      else this.studio.usp?.play(this.silenced ? 'draw' : 'draw_unsil');
    }
    this.rig?.pistol.resetPose();
  }

  setSilenced(on: boolean) {
    this.silenced = on;
    this.studio?.usp?.setIdle(on ? 'idle' : 'idle_unsil');
    this.pistol.silencer.visible = on;
    this.pistol.muzzle.position.z = on ? -0.5 : -0.3;
    this.rig?.pistol.setSilenced(on);
  }

  /** Pistol shot: slide back, kick, muzzle flash (small when silenced). */
  fire(silenced: boolean, last = false) {
    if (this.studio) {
      const n = last ? 'shootlast' : 'shoot' + (1 + Math.floor(Math.random() * 3));
      this.studio.usp?.play(silenced ? n : n + '_unsil');
    }
    if (this.rig) {
      const p = this.rig.pistol;
      p.idle.stop();
      p.reload.stop();
      p.shoot.reset().setEffectiveTimeScale(1.6).play();
    }
    this.fireT = 0;
    this.flashLeft = silenced ? 0 : 0.045;
    this.flash.scale.setScalar(1);
  }

  reload(seconds: number) {
    this.studio?.usp?.play(this.silenced ? 'reload' : 'reload_unsil', seconds);
    this.reloadT = 0;
    this.reloadDur = seconds;
    this.rareReload = Math.random() < 1 / 6;
    if (this.rig) {
      const p = this.rig.pistol;
      p.idle.stop();
      p.shoot.stop();
      p.reload
        .reset()
        .setEffectiveTimeScale(p.reloadClipSeconds / Math.max(0.1, seconds))
        .play();
    }
  }

  /** Silencer on/off animation; `after` is the state at the end. */
  silencer(seconds: number, after: boolean) {
    this.silencerT = 0;
    this.silencerDur = seconds;
    this.silencerAfter = after;
    if (this.studio) {
      this.studio.usp?.play(after ? 'add_silencer' : 'detach_silencer', seconds);
      this.silenced = after;
      this.studio.usp?.setIdle(after ? 'idle' : 'idle_unsil');
    }
  }

  private playKnife(a: KnifeAnim) {
    // The draw starts out of view, so there's nothing to blend from.
    this.knifeBlend = a !== 'draw' && a !== 'idle';
    for (let i = 0; i < POSE_SIZE; i++) this.knifeFrom[i] = this.knifePose[i];
    // Twirls end a full turn round; blend the short way.
    const tw = this.knifeFrom[P_TWIST];
    this.knifeFrom[P_TWIST] = tw - Math.round(tw / (Math.PI * 2)) * Math.PI * 2;
    this.knifeAnim = a;
    this.knifeT = 0;
  }

  inspect() {
    // CS:S has no inspect animation.
    if (this.studioActive) return;
    if (this.weapon === 'usp') {
      if (this.rig && this.fireT < 0 && this.reloadT < 0 && this.silencerT < 0 && this.pistolInspectT < 0)
        this.pistolInspectT = 0;
      return;
    }
    if (this.rig) {
      if (this.knifeAnim === 'idle') this.playKnife('inspect');
      return;
    }
    if (this.st.attack < 0 && this.st.inspect < 0) this.st.inspect = 0;
  }

  /** 0 = slash (Mouse1), 1 = stab (Mouse2). */
  attack(kind: number) {
    if (this.weapon !== 'knife') return;
    if (this.studio) {
      this.studio.knife?.play(kind === 1 ? 'stab' : this.slash++ % 2 ? 'midslash2' : 'midslash1');
    }
    // Slashes alternate forehand and backhand, like CS:S's midslash1/midslash2.
    if (this.rig) {
      this.playKnife(kind === 1 ? 'stab' : this.slash++ % 2 ? 'slash2' : 'slash1');
      return;
    }
    // Held attack keeps swinging: each swing restarts the animation.
    const st = this.st;
    st.attack = 0;
    st.kind = kind;
    st.inspect = -1;
  }

  update(dt: number, p: ViewmodelInput) {
    const st = this.st;
    const hs = p.speed;
    st.swayX *= Math.exp(-dt * 9);
    st.swayY *= Math.exp(-dt * 9);
    if (p.onGround && hs > 5) st.bob += dt * (6 + hs * 0.02);
    const bobAmp = p.onGround ? Math.min(hs / 250, 1) * 0.008 : 0;
    if (p.landImpact > 0) st.land = Math.min(0.05, st.land + p.landImpact * 0.00006);
    st.land *= Math.exp(-dt * 10);
    const air = p.onGround ? 0 : Math.max(-0.03, Math.min(0.03, -p.vz * 0.00006));
    let px = REST.x - st.swayX + Math.sin(st.bob) * bobAmp;
    let py = REST.y + st.swayY - Math.abs(Math.cos(st.bob)) * bobAmp * 0.7 - st.land + air;
    let pz = REST.z;
    let rx = REST.rx;
    let ry = REST.ry;
    let rz = REST.rz;
    let sp = 0;
    if (this.rig && this.weapon === 'knife' && !this.studioActive) {
      // Rig knife: keyframed arm and wrist; bob, sway and the landing dip move the whole arm.
      this.root.position.set(px, py, pz);
      this.tilt.rotation.set(rx, ry, rz);
      this.spin.rotation.y = 0;
      this.knifeT += dt;
      if (this.knifeAnim !== 'idle' && this.knifeT >= animLength(this.knifeAnim)) this.playKnife('idle');
      const pose = this.knifePose;
      sampleKnife(this.knifeAnim, this.knifeT, pose);
      if (this.knifeBlend) {
        const f = ease(this.knifeT / KNIFE_BLEND);
        if (f >= 1) this.knifeBlend = false;
        else
          for (let i = 0; i < POSE_SIZE; i++) pose[i] = this.knifeFrom[i] + (pose[i] - this.knifeFrom[i]) * f;
      }
      this.idleClock += dt;
      if (this.swing) this.swing.rotation.z = this.knifeAnim === 'inspect' ? butterflyFlip(this.knifeT) : 0;
      this.rig.knife.pose(pose, this.idleClock);
      return;
    }
    if (st.inspect >= 0) {
      st.inspect += dt / 2.6;
      const t = st.inspect;
      const lift = ease(t / 0.15) * (1 - ease((t - 0.85) / 0.15));
      px -= 0.2 * lift;
      py += 0.12 * lift;
      pz += 0.08 * lift;
      rz -= 0.5 * lift;
      rx += 0.2 * lift;
      sp = Math.PI * ease((t - 0.18) / 0.25) + Math.PI * ease((t - 0.55) / 0.25);
      if (t >= 1) st.inspect = -1;
    }
    if (st.attack >= 0) {
      const dur = st.kind === 0 ? 0.42 : 0.65;
      st.attack += dt / dur;
      const t = st.attack;
      const s = Math.sin(Math.PI * Math.min(t, 1));
      if (st.kind === 0) {
        px -= 0.32 * s;
        py += 0.06 * s;
        rz += 1.0 * s;
        ry -= 0.4 * s;
      } else {
        pz -= 0.28 * s;
        py += 0.08 * s;
        rx -= 0.6 * s;
        px -= 0.12 * s;
      }
      if (t >= 1) st.attack = -1;
    }
    if (this.studioActive) {
      // CS:S models: their own animations, plus our bob, sway and landing dip.
      const studio = this.studio!;
      studio.offset.position.set((px - REST.x) * 0.6, (py - REST.y) * 0.6, 0);
      st.inspect = st.attack = -1;
      studio[this.weapon]?.update(dt);
      this.flashLeft -= dt;
      studio.flash.visible = this.flashLeft > 0 && this.weapon === 'usp';
      if (studio.flash.visible) studio.flash.rotation.z = Math.random() * Math.PI;
      return;
    }
    // Raise after switching weapons.
    this.raise = Math.min(1, this.raise + dt / this.raiseTime);
    const lower = 1 - ease(this.raise);
    this.root.position.set(px, py - lower * 0.35, pz);
    this.tilt.rotation.set(rx - lower * 0.6, ry, rz);
    this.spin.rotation.y = sp;
    if (this.weapon === 'usp') this.updatePistol(dt, px - REST.x, py - REST.y, lower);
  }

  private updatePistol(dt: number, bx: number, by: number, lower: number) {
    if (this.rig) {
      this.updateRigPistol(dt, bx, by, lower);
      return;
    }
    const g = this.pistolRoot;
    let x = 0.15 + bx * 0.8;
    let y = -0.16 + by * 0.8 - lower * 0.35;
    let z = -0.52;
    let rx = -lower * 0.7;
    let ry = 0.06;
    let rz = 0;
    // Shot: slide recoils, gun kicks up and back.
    this.pistol.slide.position.z = 0;
    if (this.fireT >= 0) {
      this.fireT += dt;
      const t = this.fireT;
      const kick = t < 0.03 ? t / 0.03 : Math.max(0, 1 - (t - 0.03) / 0.12);
      rx += kick * 0.22;
      z += kick * 0.035;
      this.pistol.slide.position.z = t < 0.07 ? Math.sin((t / 0.07) * Math.PI) * 0.045 : 0;
      if (t > 0.2) this.fireT = -1;
    }
    this.flashLeft -= dt;
    this.flash.visible = this.flashLeft > 0;
    if (this.flash.visible) this.flash.rotation.z = Math.random() * Math.PI;
    // Reload: tilt, magazine drops and a new one goes in, slide release.
    this.pistol.mag.position.y = -0.155;
    this.pistol.mag.visible = true;
    if (this.reloadT >= 0) {
      this.reloadT += dt;
      const t = this.reloadT / this.reloadDur;
      const tilt = ease(t / 0.15) * (1 - ease((t - 0.85) / 0.15));
      rz += tilt * 0.6;
      rx += tilt * 0.25;
      y -= tilt * 0.05;
      if (t > 0.15 && t < 0.35) this.pistol.mag.position.y = -0.155 - ((t - 0.15) / 0.2) * 0.3;
      else if (t >= 0.35 && t < 0.45) this.pistol.mag.visible = false;
      else if (t >= 0.45 && t < 0.7) this.pistol.mag.position.y = -0.155 - (1 - (t - 0.45) / 0.25) * 0.25;
      if (t > 0.78 && t < 0.86) this.pistol.slide.position.z = 0.04;
      if (t >= 1) this.reloadT = -1;
    }
    // Silencer: turn the gun, twist the silencer off/on.
    if (this.silencerT >= 0) {
      this.silencerT += dt;
      const t = this.silencerT / this.silencerDur;
      const turn = ease(t / 0.2) * (1 - ease((t - 0.8) / 0.2));
      ry -= turn * 0.9;
      rx += turn * 0.2;
      x -= turn * 0.08;
      if (t > 0.5) this.setSilenced(this.silencerAfter);
      if (t >= 1) this.silencerT = -1;
    }
    g.position.set(x, y, z);
    g.rotation.set(rx, ry, rz);
  }

  /** The rig's Glock: its own shoot/reload animations; bob, sway, raise and the silencer turn here. */
  private updateRigPistol(dt: number, bx: number, by: number, lower: number) {
    const p = this.rig!.pistol;
    p.mixer.update(dt);
    // The gun turns in the hand (the arm follows). At rest it's angled up and in, like CS2's USP-S.
    let yaw = PISTOL_REST_YAW;
    let roll = 0;
    let lift = 0;
    let pitch = PISTOL_REST_PITCH;
    if (this.fireT >= 0 || this.reloadT >= 0 || this.silencerT >= 0) this.pistolInspectT = -1;
    if (this.pistolInspectT >= 0) {
      // Inspect: show the left side, tip a little further, come back.
      this.pistolInspectT += dt;
      const t = this.pistolInspectT / PISTOL_INSPECT;
      const a = ease(t / 0.22) * (1 - ease((t - 0.78) / 0.22));
      const tilt = ease((t - 0.4) / 0.15) * (1 - ease((t - 0.62) / 0.15));
      yaw += 0.95 * a - 0.25 * tilt;
      roll += 0.3 * a + 0.35 * tilt;
      lift += 0.035 * a;
      if (t >= 1) this.pistolInspectT = -1;
    }
    if (this.reloadT >= 0) {
      // Reload: tip the gun onto its side; now and then flip it up toward the sky instead.
      const t = Math.min(1, this.reloadT / this.reloadDur);
      const a = ease(t / 0.15) * (1 - ease((t - 0.8) / 0.2));
      if (this.rareReload) {
        pitch += 1.05 * a;
        yaw += 0.4 * a;
        roll -= 0.1 * a;
        lift += 0.02 * a;
      } else {
        yaw += 0.35 * a;
        roll += 0.5 * a;
      }
    }
    p.turnGun(yaw, roll, lift, pitch);
    // Two-handed: the support hand holds the gun too.
    p.arms?.showSupport(true);
    p.showMagazine(this.reloadT >= 0);
    p.arms?.follow();
    let x = bx * 0.8;
    let y = by * 0.8 - lower * 0.35;
    let rx = -lower * 0.7;
    let ry = 0;
    if (this.fireT >= 0) {
      this.fireT += dt;
      if (this.fireT > 0.2) this.fireT = -1;
    }
    if (this.reloadT >= 0) {
      this.reloadT += dt;
      if (this.reloadT >= this.reloadDur) this.reloadT = -1;
    }
    if (this.silencerT >= 0) {
      this.silencerT += dt;
      const t = this.silencerT / this.silencerDur;
      const turn = ease(t / 0.2) * (1 - ease((t - 0.8) / 0.2));
      ry -= turn * 0.6;
      rx += turn * 0.25;
      x -= turn * 0.05;
      y -= turn * 0.04;
      if (t > 0.5 && this.silenced !== this.silencerAfter) this.setSilenced(this.silencerAfter);
      if (t >= 1) this.silencerT = -1;
    }
    this.flashLeft -= dt;
    this.flash.visible = this.flashLeft > 0;
    if (this.flash.visible) this.flash.rotation.z = Math.random() * Math.PI;
    // Turn around the gun: position = offset + pivot - R * pivot.
    this.turnQ.setFromEuler(this.turnE.set(rx, ry, 0));
    this.turnV.copy(this.gunPivot).applyQuaternion(this.turnQ);
    p.object.quaternion.copy(this.turnQ);
    p.object.position.set(x, y, 0).add(this.gunPivot).sub(this.turnV);
  }
}
